import { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import IsoDateInput from "../common/Isodateinput";

const PRIMARY = "#1B5C20";

const loanTypes = [
  { value: "Personal", label: "Personal" },
  { value: "Housing", label: "Housing" },
  { value: "Vehicle", label: "Vehicle" },
  { value: "Emergency", label: "Emergency" },
  { value: "Education", label: "Education" },
  { value: "Other", label: "Other" },
];

const initialForm = {
  employeeId: "",
  loanNo: "",
  loanType: "",
  dueDate: "",
  loanDescription: "",
  loanDate: new Date().toISOString().split("T")[0],
  loanAmount: "",
  interestRate: "",
  totalInterest: "",
  totalAmount: "",
  noOfInstallments: "",
  installmentAmount: "",
  startPeriodId: null,
  balanceAmount: "",
  status: "P",
  remarks: "",
};

const inputClass = `
  w-full h-14
  rounded-md
  border border-surface-200
  bg-white
  px-3.5
  text-[16px] text-surface-800
  outline-none
  transition-colors
  focus:border-primary-500
  focus:ring-1 focus:ring-primary-500
`;

const labelClass = `
  absolute
  left-3
  top-0
  -translate-y-1/2
  bg-white
  px-1.5
  text-[13px]
  text-surface-500
  pointer-events-none
  z-10
`;

const errorClass = "!border-red-400 focus:!border-red-500 focus:!ring-red-500";

const maskCurrencyInput = (rawValue) => {
  const digits = String(rawValue ?? "").replace(/\D/g, "");

  if (!digits) return "";

  const padded = digits.padStart(3, "0");
  const cents = padded.slice(-2);

  const wholeDigits = padded.slice(0, -2).replace(/^0+(?=\d)/, "");
  const whole = Number(wholeDigits || "0").toLocaleString("en-US");

  return `${whole}.${cents}`;
};

const parseCurrency = (value) => {
  if (value === "" || value === null || value === undefined) return 0;
  const n = Number(String(value).replace(/,/g, ""));
  return Number.isNaN(n) ? 0 : n;
};

function Field({ label, children, error = ""}) {
  return (
    <div className="relative">
      <label className={labelClass}>
        {label}
      </label>

      {children}

      {error && (
        <p className="mt-1.5 flex items-start gap-1 text-xs text-red-600">
          {error}
        </p>
      )}
    </div>
  );
}

function TextInput({
  label,
  value,
  onChange,
  type = "text",
  min,
  disabled = false,
  readOnly = false,
  helperText,
  helperError = false,
  error = "",
}) {
  return (
    <div>
      <Field label={label} error={error}>
        <input
          type={type}
          value={value}
          onChange={onChange}
          disabled={disabled}
          readOnly={readOnly}
          min={min}
          className={`
            ${inputClass}
            ${readOnly ? "bg-surface-50" : ""}
            ${disabled
              ? "cursor-not-allowed bg-surface-50 text-surface-400"
              : ""}
            ${
              error ? errorClass : ""
            }
          `}
        />
      </Field>

      {helperText && (
        <p
          className={`
            mt-1 px-1 text-[12px]
            ${helperError ? "text-[#C0392B]" : "text-surface-500"}
          `}
        >
          {helperText}
        </p>
      )}
    </div>
  );
}

function DateInput({ label, value, onChange, error = "" }) {
  return (
    <Field label={label} error={error}>
      <IsoDateInput
        value={value}
        onChange={(valueOrEvent) => {
          const value =
            valueOrEvent?.target !== undefined
              ? valueOrEvent.target.value
              : valueOrEvent;

          onChange({
            target: {
              value,
            },
          });
        }}
        className={`${inputClass} appearance-none ${
          error ? errorClass : ""
        }`}
      />
    </Field>
  );
}

function SelectInput({
  label,
  value,
  onChange,
  children,
  error = ""
}) {
  return (
    <Field label={label} error={error}>
      <select
        value={value}
        onChange={onChange}
        className={`${inputClass} appearance-auto cursor-pointer ${
          error ? errorClass : ""
        }`}
      >
        {children}
      </select>
    </Field>
  );
}

function TextAreaInput({
  label,
  value,
  onChange,
  rows = 2,
  error = ""
}) {
  return (
    <Field label={label} error={error}>
      <textarea
        value={value}
        onChange={onChange}
        rows={rows}
        className={`
          w-full
          rounded-md
          border border-surface-200
          bg-white
          px-3.5
          py-3
          text-[16px]
          text-surface-800
          outline-none
          transition-colors
          focus:border-primary-500
          focus:ring-1
          focus:ring-primary-500
          ${error ? errorClass : ""}
        `}
      />
    </Field>
  );
}

function CurrencyInput({
  label,
  value,
  onChange,
  disabled = false,
  readOnly = false,
  helperText,
  error = "",
}) {
  // If the field is read-only (calculated), we receive a raw number and format it.
  // If the field is editable, the value is already masked via state.
  let displayValue = value ?? "";
  if (readOnly && displayValue !== "") {
    const num = Number(displayValue);
    if (!Number.isNaN(num)) {
      displayValue = num.toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    }
  }

  const handleChange = (e) => {
    if (!readOnly && onChange) {
      const masked = maskCurrencyInput(e.target.value);
      onChange({
        target: {
          value: masked,
        },
      });
    }
  };

  return (
    <div>
      <Field label={label} error={error}>
        <input
          type="text"
          inputMode="numeric"
          value={displayValue}
          onChange={handleChange}
          disabled={disabled}
          readOnly={readOnly}
          className={`
            ${inputClass}
            ${readOnly ? "bg-surface-50" : ""}
            ${
              disabled
                ? "cursor-not-allowed bg-surface-50 text-surface-400"
                : ""
            }
            ${error ? errorClass : ""}
          `}
        />
      </Field>

      {helperText && (
        <p className="mt-1 px-1 text-[12px] text-surface-500">
          {helperText}
        </p>
      )}
    </div>
  );
}

export default function AddLoanDialog({
  open,
  onClose,
  onSave,
  loading,
  employees = [],
  payrollPeriods = [],
  initialData,
  nextLoanNo = "",
}) {
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!open) return;
    setErrors({});

    // Helper to format local date safely and avoid the UTC timezone offset shift
    const formatLocalDate = (dateStr) => {
      if (!dateStr) return "";
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return dateStr;
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    };

    if (initialData) {
      setForm({
        id: initialData.id,
        employeeId: initialData.employeeId || "",
        loanNo: initialData.loanNo || "",
        loanType: initialData.loanType || "",
        loanDescription: initialData.loanDescription || "",
        loanDate: formatLocalDate(initialData.loanDate),
        loanAmount: initialData.amount != null 
          ? maskCurrencyInput(String(Math.round(Number(initialData.amount) * 100))) 
          : "",
        interestRate: initialData.interestRate ?? "",
        totalInterest: initialData.totalInterest ?? "",
        totalAmount: initialData.totalAmount ?? "",
        noOfInstallments: initialData.installments ?? "",
        installmentAmount: initialData.installmentAmount ?? "",
        startPeriodId: initialData.startPeriodId || "",
        balanceAmount: initialData.balance ?? "",
        // Raw HLM_STATUS code (e.g. "P"), not the display label.
        status: initialData.statusCode || (initialData.status === "Not Paid" ? "P" : initialData.status) || "P",
        statusCode: initialData.statusCode || (initialData.status === "Not Paid" ? "P" : initialData.status) || "P",
        remarks: initialData.remarks || "",
        isActive: initialData.isActive,
        createdBy: initialData.createdBy,
      });
    } else {
      setForm({
        ...initialForm,
      });
    }
  }, [open, initialData]);

  // Recalculate loan totals whenever the amount,
  // interest rate, or installment count changes.
  useEffect(() => {
    const amount = parseCurrency(form.loanAmount);
    const rate = Number(form.interestRate || 0);
    const installments = Number(form.noOfInstallments || 0);

    const totalInterest =
      Math.round(((amount * rate) / 100) * 100) / 100;

    const totalAmount =
      Math.round((amount + totalInterest) * 100) / 100;

    const installmentAmount =
      installments > 0
        ? Math.round((totalAmount / installments) * 100) / 100
        : "";

    // New loan: balance starts at the loan amount.
    // Editing: keep what has already been paid off — move the saved balance
    // by the change in loan amount only (never below 0).
    let balanceAmount = amount;
    if (initialData) {
      const originalAmount = Number(initialData.amount || 0);
      const originalBalance = Number(initialData.balance ?? originalAmount);
      balanceAmount = Math.max(
        0,
        Math.round((originalBalance + (amount - originalAmount)) * 100) / 100
      );
    }

    setForm((prev) => ({
      ...prev,
      totalInterest,
      totalAmount,
      installmentAmount,
      balanceAmount,
    }));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    form.loanAmount,
    form.interestRate,
    form.noOfInstallments,
  ]);

  // const handleChange = (field) => (e) => {
  //   if (initialData && field === "noOfInstallments") {
  //     return;
  //   }

  //   setForm((prev) => ({
  //     ...prev,
  //     [field]: e.target.value,
  //   }));

  //   setErrors((prev) => ({
  //     ...prev,
  //     [field]: ""
  //   }))
  // };

  const handleChange = (field) => (e) => {
  setForm((prev) => ({
    ...prev,
    [field]: e.target.value,
  }));

  setErrors((prev) => ({
    ...prev,
    [field]: "",
  }));
};

  const validate = () => {
    const newErrors = {};

    if (!form.employeeId) {
      newErrors.employeeId = "Employee is required.";
    }

    if (!form.loanType) {
      newErrors.loanType = "Loan type is required.";
    }

    const amount = parseCurrency(form.loanAmount);

    if (!form.loanAmount || amount <= 0) {
      newErrors.loanAmount = "Loan amount must be greater than 0.";
    }

    const rate = Number(form.interestRate);

    if (form.interestRate === "" || Number.isNaN(rate) || rate < 0) {
      newErrors.interestRate = "Interest rate cannot be negative.";
    } else if (rate > 100) {
      newErrors.interestRate = "Interest rate cannot be above 100.";
    }

    const installments = Number(form.noOfInstallments);

    if (form.noOfInstallments === "" || Number.isNaN(installments) || installments <= 0) {
      newErrors.noOfInstallments = "Number of installments must be greater than 0.";
    }

    // if (!form.startPeriodId) {
    //   newErrors.startPeriodId = "Payroll period is required.";
    // }

    if (!initialData && form.loanDate) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const selectedDate = new Date(form.loanDate);
      selectedDate.setHours(0, 0, 0, 0);

      // if (selectedDate < today) {
      //   newErrors.loanDate = "Loan date cannot be in the past";
      // }
    }

    if (!initialData && !form.dueDate) {
      newErrors.dueDate = "Installment due date is required.";
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  }

  const handleSave = () => {
    if (!validate()) return;

    onSave({
      ...form,
      loanAmount: parseCurrency(form.loanAmount),
      isActive: initialData ? (form.isActive ?? false) : false,
    });
  };

  const loanAmount = parseCurrency(form.loanAmount);
  const interestRate = Number(form.interestRate || 0);
  const installments = Number(form.noOfInstallments || 0);

  const loanAmountMissing = loanAmount <= 0;
  const interestRateInvalid = interestRate < 0;
  const installmentsInvalid = installments < 0;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle className="!px-6 !pt-5 !pb-3">
        <span className="text-[21px] font-medium text-surface-900">
          {initialData ? "Update Loan" : "New Loan"}
        </span>
      </DialogTitle>

      <DialogContent className="!px-6 !py-5">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">

          {/* Employee */}
          <SelectInput
            label="Employee"
            value={form.employeeId}
            error={errors.employeeId}
            onChange={handleChange("employeeId")}
          >
            <option value="">
              Select Employee
            </option>

            {employees.map((emp) => (
              <option
                key={emp.HED_EMPLOYEE_ID}
                value={emp.HED_EMPLOYEE_ID}
              >
                {emp.HED_REPORT_NAME || "-"} ({emp.HED_SERVICE_NO})
              </option>
            ))}
          </SelectInput>

          {/* Loan Number */}
          <TextInput
            label="Loan Number"
            value={initialData ? form.loanNo : nextLoanNo}
            readOnly
            helperText={
              initialData ? "Loan number cannot be changed." : "Automatically generated."
            }
          />

          {/* Loan Type */}
          <SelectInput
            label="Loan Type"
            value={form.loanType}
            error={errors.loanType}
            onChange={handleChange("loanType")}
          >
            <option value="">
              Select Loan Type
            </option>

            {loanTypes.map((type) => (
              <option
                key={type.value}
                value={type.value}
              >
                {type.label}
              </option>
            ))}
          </SelectInput>

          {/* Loan Date */}
          <DateInput
            label="Loan Date"
            value={form.loanDate}
            error={errors.loanDate}
            onChange={handleChange("loanDate")}
          />

          {/* Loan Description */}
          <div className="sm:col-span-2">
            <TextAreaInput
              label="Loan Description"
              value={form.loanDescription}
              error={errors.loanDescription}
              onChange={handleChange("loanDescription")}
              rows={1}
            />
          </div>

          {/* Loan Amount */}
          <CurrencyInput
            label="Loan Amount"
            value={form.loanAmount}
            error={errors.loanAmount}
            onChange={handleChange("loanAmount")}
            // readOnly={!!initialData}
            helperText={
              initialData ? "Loan amount cannot be changed after loan is created."
              : undefined
            }
          />

          {/* Interest Rate */}
          <TextInput
            label="Interest Rate (%)"
            type="number"
            min={0}
            value={form.interestRate}
            error={errors.interestRate}
            onChange={handleChange("interestRate")}
          />

          {/* Number of Installments */}
          <TextInput
            label="No. of Installments"
            type="number"
            min={0}
            value={form.noOfInstallments}
            error={errors.noOfInstallments}
            onChange={handleChange("noOfInstallments")}
            // readOnly={!!initialData}
            helperText={
              initialData
                ? "Updates the loan only — existing installment rows are not regenerated."
                : undefined
            }
          />

          {/* Start Payroll Period */}
          {/* <SelectInput
            label="Start Payroll Period"
            value={form.startPeriodId}
            error={errors.startPeriodId}
            onChange={handleChange("startPeriodId")}
          >
            <option value="">
              Select Payroll Period
            </option>

            {payrollPeriods
              .filter((period) => period.status === "Active")
              .map((period) => (
                <option
                  key={period.id}
                  value={period.id}
                >
                  {period.code} - {period.name}
                </option>
              ))
            }
          </SelectInput> */}

          {/* Installment Due Date */}
          {!initialData && (
            <DateInput
              label="First Installment Due Date"
              value={form.dueDate}
              error={errors.dueDate}
              onChange={handleChange("dueDate")}
            />
          )}

          {/* Total Interest */}
          <CurrencyInput
            label="Total Interest"
            value={form.totalInterest}
            readOnly
            helperText="Loan Amount × Interest Rate ÷ 100"
          />

          {/* Total Amount */}
          <CurrencyInput
            label="Total Amount"
            value={form.totalAmount}
            readOnly
            helperText="Loan Amount + Total Interest"
          />

          {/* Installment Amount */}
          <CurrencyInput
            label="Installment Amount"
            value={form.installmentAmount}
            readOnly
            helperText="Total Amount ÷ No. of Installments"
          />

          {/* Balance Amount */}
          <CurrencyInput
            label="Balance Amount"
            value={form.balanceAmount}
            readOnly
            helperText={
              initialData
                ? "Saved balance, adjusted by any change in loan amount"
                : "Initial outstanding loan balance"
            }
          />

          {/* <TextInput
            label="Remarks"
            value={form.remarks}
            error={errors.remarks}
            onChange={handleChange("remarks")}
          /> */}
          
          {/* <div className="sm:col-span-2">
            <TextAreaInput
              label="Remarks"
              value={form.remarks}
              onChange={handleChange("remarks")}
              rows={2}
            />
          </div> */}

          {/* Remarks */}
          {initialData ? (
            <TextInput
              label="Remarks"
              value={form.remarks}
              error={errors.remarks}
              onChange={handleChange("remarks")}
            />
          ) : (
            <div className="sm:col-span-2">
              <TextAreaInput
                label="Remarks"
                value={form.remarks}
                onChange={handleChange("remarks")}
                rows={2}
              />
            </div>
          )}

          {/* Calculation Info */}
          {loanAmount > 0 &&
            interestRate >= 0 &&
            installments > 0 && (
              <div className="sm:col-span-2">
                <div className="rounded-lg bg-[#F0F7F0] p-3">
                  <p className="text-[12px] text-[#1B5C20]">
                    Loan summary: Total loan amount is{" "}
                    <strong>{form.totalAmount || 0}</strong>,
                    payable in{" "}
                    <strong>{installments}</strong>{" "}
                    installments of{" "}
                    <strong>{form.installmentAmount || 0}</strong>.
                  </p>
                </div>
              </div>
            )}
        </div>
      </DialogContent>

      <DialogActions className="!px-6 !py-3">
        <Button
          onClick={onClose}
          className="!text-primary-600"
        >
          CANCEL
        </Button>

        <Button
          variant="contained"
          onClick={handleSave}
          disabled={
            loading ||
            !form.employeeId ||
            !form.loanType 
            // loanAmountMissing ||
            // interestRateInvalid ||
            // installmentsInvalid ||
            // !form.startPeriodId ||
            // !form.dueDate
          }
          className="!bg-primary-600 hover:!bg-primary-700"
        >
          {loading ? "SAVING..." : "SAVE"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}